import { useEffect, useMemo } from "react";
import { XIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  ClearableSearchInput,
  Column,
  Row,
  Text,
  TopbarContainer,
  TopbarTitle,
} from "@/components";
import { PipelineHealthScoreCard } from "@/components/portfolio/PipelineHealthScoreCard";
import { PortfolioTrends } from "@/components/portfolio/PortfolioTrendsSection";
import { FilterDropdown } from "@/components/portfolio/table/FilterDropdown";
import { PortfolioTable } from "@/components/portfolio/table/PortfolioTable";
import { usePortfolioFilters } from "@/components/portfolio/table/usePortfolioFilters";
import { WorkloadSignalCard } from "@/components/portfolio/WorkloadSignalCard";

import { useSetTitle } from "@/hooks/useSetTitle";
import { useGetPortfolioMatrix } from "@/hooks/queries/portfolio/useGetPortfolioMatrix";
import { getHealthTier } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { PortfolioHealth, PortfolioHealthScoreTier } from "@/types";

const SectionLabel = ({ children }: { children: string }) => (
  <Text className="pb-2 text-xs font-semibold uppercase leading-none tracking-widest text-slate-500">
    {children}
  </Text>
);

export const PortfolioPage = () => {
  const matrixApi = useGetPortfolioMatrix();
  const {
    filters,
    filteredJobs,
    stafferFilteredJobs,
    staffer,
    sales,
    stafferFirstName,
    salesFirstName,
    scopedStafferIds,
    jobTitleSearch,
    setJobTitleSearch,
  } = usePortfolioFilters(matrixApi.data);

  const healthFilter =
    filters.find((f) => f.label === "Job Health") ?? filters[0];
  const topFilters = filters.filter(
    (f) => f.label === "Staffer" || f.label === "Sales",
  );
  const tableFilters = filters.filter(
    (f) => f.label !== "Staffer" && f.label !== "Sales",
  );

  const healthCounts = useMemo<PortfolioHealth>(() => {
    const counts: PortfolioHealth = {
      healthy: 0,
      watch: 0,
      "at-risk": 0,
      critical: 0,
    };
    for (const job of stafferFilteredJobs) {
      counts[getHealthTier(job.score)] += 1;
    }
    return counts;
  }, [stafferFilteredJobs]);

  const jobsWithWarnings =
    healthCounts["at-risk"] + healthCounts.critical + healthCounts.watch;

  useSetTitle({ title: "Portfolio Overview" });

  useEffect(() => {
    mixpanelUtils.trackScreenView(MIXPANEL_SCREENS.JOB_HEALH);
  }, []);

  return (
    <Column className="h-svh min-w-0 flex-1 overflow-y-auto bg-slate-50">
      <TopbarContainer className="!h-auto border-b-0 !pb-3 !pt-9">
        <Row className="w-full items-center justify-between gap-4">
          <TopbarTitle className="text-xl font-semibold text-slate-900">
            Job Health
          </TopbarTitle>
          <Row className="gap-2">
            {topFilters.map((filter) => (
              <FilterDropdown
                key={filter.label}
                {...filter}
                showSelectedInline
                showAllOption
              />
            ))}
          </Row>
        </Row>
      </TopbarContainer>

      <Column className="flex flex-col gap-6 px-6 pb-24 pt-4">
        <Column className="min-w-0 gap-2">
          <SectionLabel>Snapshot</SectionLabel>
          <Row className="min-w-0 items-stretch gap-4">
            <PipelineHealthScoreCard
              counts={healthCounts}
              isLoading={matrixApi.isLoading}
              stafferName={stafferFirstName}
              salesName={salesFirstName}
              selectedTier={
                healthFilter.selected as PortfolioHealthScoreTier | null
              }
              onTierSelect={healthFilter.onToggle}
            />

            <WorkloadSignalCard
              scopedStafferIds={scopedStafferIds}
              stafferName={stafferFirstName}
              salesName={salesFirstName}
              jobsWithWarnings={jobsWithWarnings}
            />
          </Row>
        </Column>

        <PortfolioTrends
          isDisabled={staffer != null || sales != null}
          stafferId={staffer}
          salesId={sales}
        />

        <Column className="min-w-0 gap-2">
          <Row className="items-end justify-between gap-4">
            <SectionLabel>Job Health</SectionLabel>
            <Row className="flex-wrap items-center justify-between gap-2">
              {tableFilters.some((filter) => filter.selected != null) && (
                <Row className="shrink-0 flex-wrap items-center gap-1.5">
                  {tableFilters
                    .filter((filter) => filter.selected != null)
                    .map((filter) => {
                      const selectedLabel =
                        filter.options.find(
                          (option) => option.value === filter.selected,
                        )?.label ?? filter.selected;

                      return (
                        <Row
                          key={filter.label}
                          className="h-9 max-w-[260px] shrink-0 items-center gap-1 rounded-full border border-primary-400 bg-primary-50 px-3 py-4"
                        >
                          <Text className="truncate text-xs font-medium text-primary-900">
                            {filter.label}: {selectedLabel}
                          </Text>
                          <Button
                            type="button"
                            aria-label={`Clear ${filter.label} filter`}
                            className="h-5 w-5 shrink-0 rounded-full p-0 text-primary-700 hover:bg-primary-100"
                            onClick={filter.onClear}
                            size="icon"
                            variant="ghost"
                          >
                            <XIcon className="h-3 w-3" />
                          </Button>
                        </Row>
                      );
                    })}
                </Row>
              )}
              <Row className="gap-2">
                {tableFilters.map((filter) => (
                  <FilterDropdown key={filter.label} {...filter} />
                ))}
                <ClearableSearchInput
                  containerClassName="w-[165px]"
                  className="h-9 rounded-md text-sm"
                  onChange={setJobTitleSearch}
                  placeholder="Search jobs"
                  value={jobTitleSearch}
                />
              </Row>
            </Row>
          </Row>
          <PortfolioTable
            jobs={filteredJobs}
            isLoading={matrixApi.isLoading}
            isError={matrixApi.isError}
          />
        </Column>
      </Column>
    </Column>
  );
};
